import axios from 'axios';
import store from 'store';
import { FETCH_USER, WAITING, ERROR, FETCH_GOOGLE_PLACES } from './types';
import notification from '../utils/notification';
import clearCookies from '../utils/clearCookies';

export const fetchUser = () => async (dispatch) => {
	dispatch({ type: WAITING, payload: true });
	// get user details
	const res = await axios.get('/api/current_user');

	// get current hostname
	const currentHost = window.location.hostname;

	// make sure user is active
	if (!res.data || !res.data.user || !res.data.user.active) {
		// set user to res.data (should be undefined or user object)
		dispatch({ type: FETCH_USER, payload: null });
		dispatch({ type: WAITING, payload: false });

		if (res.data && res.data.user && !res.data.user.active) {
			dispatch(
				notification(
					'Your account has been canceled.',
					'If you think this is an error, contact your administrator.',
					true
				)
			);
		}

		return true;
	}

	// check if user is owned by a reseller
	const resellerAccount = res && res.data && res.data.reseller;

	// if the client is a reseller account and the current host includes chekkit, redirect them to the proper whitelabel url
	if (resellerAccount && currentHost.includes('chekkit.')) {
		// get the proper domain
		const whitelabelHostnameRes = await axios.get(
			`/api/whitelabel-hostname/${resellerAccount}`
		);

		// make sure it exists
		const resellerHostname =
			whitelabelHostnameRes && whitelabelHostnameRes.data;

		// redirect to the whitelabeled domain
		if (resellerHostname && !resellerHostname.includes('chekkit')) {
			window.location = `https://${resellerHostname}`;
			return false;
		}
	}

	// set user to res.data (should be undefined or user object)
	dispatch({ type: FETCH_USER, payload: res.data });

	dispatch({ type: WAITING, payload: false });

	return true;
};

export const confirmEmail = (token) => async (dispatch) => {
	dispatch({ type: ERROR, payload: false });

	const res = await axios.post('/api/confirm-registration', { token });

	if (!res.data) {
		return;
	}

	// navigate to login page
	window.location.href = '/login';

	dispatch({
		type: ERROR,
		payload: res.data.message,
	});

	return dispatch({ type: FETCH_USER, payload: null });
};

export const resellerRegisterUser = (user) => async (dispatch) => {
	dispatch({ type: ERROR, payload: false });
	let res;
	try {
		res = await axios.post('/api/register-reseller', user);
	} catch (error) {
		return dispatch({ type: ERROR, payload: error.response?.data?.message || 'Registration could not be completed. Please try again.' });
	}

	if (res.status === 209) {
		return dispatch({ type: ERROR, payload: res.data });
	}

	// navigate to login page
	window.location.href = '/login';

	dispatch({
		type: ERROR,
		payload: `You may now log in with ${user.username}`,
	});
	return dispatch({ type: FETCH_USER, payload: null });
};

export const loginUser = (user) => async (dispatch) => {
	try {
		store.remove('jwt');

		clearCookies();
	} catch (err) {
		// clear all could fail?
		console.log(err);
	}

	dispatch({ type: ERROR, payload: false });
	const res = await axios.post('/api/login', user);

	if (res.status === 209) {
		return dispatch({ type: ERROR, payload: res.data });
	}

	if (res.status === 211) {
		return (window.location.href = 'https://www.chekkitwifi.com/login');
	}

	if (res.data.isTwoFactorAuthEnabled) {
		return {
			_business: res.data._business,
			isTwoFactorAuthEnabled: res.data.isTwoFactorAuthEnabled,
		};
	}

	// get Jwt
	const { token } = res.data;

	if (!token) {
		return dispatch({ type: ERROR, payload: 'Wrong username or password' });
	}

	// save the jwt so you can use authenticated routes
	store.set('jwt', token);

	const root = `${window.location.protocol}//${window.location.host}`;
	return (window.location.href = root);
};

export const loginUserWithTwoFactorAuthentication =
	({ _business, twoFactorAuthCode }) =>
	async (dispatch) => {
		try {
			// eslint-disable-next-line no-unreachable
			const res = await axios.post('/api/confirm-2fa-code', {
				_business,
				twoFactorAuthCode,
			});

			const { data } = res;

			if (data.error) {
				return dispatch({
					type: ERROR,
					payload: data.message || 'Invalid code, please try again',
				});
			}
			const { token } = res.data;

			if (!token) {
				return dispatch({
					type: ERROR,
					payload: 'Invalid code, please try again',
				});
			}

			// save the jwt so you can use authenticated routes
			store.set('jwt', token);

			const root = `${window.location.protocol}//${window.location.host}`;
			return (window.location.href = root);
		} catch (err) {
			console.log(err);
			return dispatch({
				type: ERROR,
				payload: 'Wrong username or password',
			});
		}
	};

export const switchUser =
	({ _business, goToRootUrl, goToBasePath = '' }) =>
	async (dispatch) => {
		try {
			store.remove('jwt');
			const res = await axios.get(`/api/switch-business/${_business}`);

			const { error, status } = res;
			const { token } = res.data;

			if (error || status === 209 || !token) {
				const title = 'Error';
				const message = 'Could not switch locations';
				return dispatch(notification(title, message, error));
			}

			// set login token
			store.set('jwt', token);

			if (goToRootUrl) {
				window.history.replaceState({}, document.title, '/');
			}

			// if a path is provided, go to the origin when switching location and neglect all params
			if (goToBasePath) {
				return window.location.replace(goToBasePath);
			}

			// otherwise preserve url location and params
			return window.location.reload();
		} catch (err) {
			console.log('Error switching locations', err);
			const title = 'Error';
			const message = 'Could not switch locations';
			const error = true;
			return dispatch(notification(title, message, error));
		}
	};

export const fetchGooglePlaces = () => async (dispatch) => {
	const res = await axios.get('/api/google-places');
	dispatch({ type: FETCH_GOOGLE_PLACES, payload: res.data });
};

export const updateBrandColor =
	({ brandColor }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-brand-color', {
				brandColor,
			});
			const { error, errorMessage } = res.data;

			const title = error ? 'Error' : 'Success';

			let message = error
				? 'There was an error updating your brand color'
				: 'We have updated your brand color!';

			if (errorMessage) {
				message = errorMessage;
			}

			dispatch(notification(title, message, error));
		} catch (err) {
			dispatch(
				notification(
					'Error',
					'Sorry we could not update your brand color.',
					true
				)
			);
		}
	};

export const handleIsReceivingUnansweredMessageEmailMinutes =
	(minutes) => async (dispatch) => {
		try {
			const res = await axios.post(
				'/api/update-is-receiving-unanswered-message-email',
				{
					minutes,
				}
			);

			if (res.data && !res.data.error) {
				dispatch({ type: FETCH_USER, payload: res.data });
			} else {
				const errorMessage =
					res.data && res.data.message
						? res.data.message
						: 'There was an error updating your settings.';

				dispatch(notification('Error', errorMessage, true));
			}
		} catch (err) {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	};

export const updateNotificationSettings =
	(notificationSettings) => async (dispatch) => {
		try {
			const res = await axios.post('/api/update-notification-settings', {
				notificationSettings,
			});

			if (res.data && !res.data.error) {
				dispatch({ type: FETCH_USER, payload: res.data });
				const title = 'Success';
				const message = 'Updated your preferences.';
				dispatch(notification(title, message, false));
			} else {
				dispatch(
					notification(
						'Error',
						'There was an error updating your settings.',
						true
					)
				);
			}
		} catch (err) {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	};

export const updateNotificationSchedule =
	({ notificationSchedule, notificationScheduleTimezone }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-notification-schedule', {
				notificationSchedule,
				notificationScheduleTimezone,
			});

			if (res.data && !res.data.error) {
				dispatch({ type: FETCH_USER, payload: res.data });
				dispatch(notification('Success', 'Updated your quiet hours.', false));
			} else {
				const errorMessage =
					res.data?.message ||
					'There was an error updating your quiet hours.';
				dispatch(
					notification(
						'Error',
						errorMessage,
						true
					)
				);
			}
		} catch (_err) {
			dispatch(
				notification(
					'Error',
					'There was an error updating your quiet hours.',
					true
				)
			);
		}
	};

export const removeNotificationSchedule = () => async (dispatch) => {
	try {
		const res = await axios.post('/api/remove-notification-schedule-from-user');

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			dispatch(notification('Success', 'Quiet hours are turned off.', false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your quiet hours.',
					true
				)
			);
		}
	} catch (_err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your quiet hours.',
				true
			)
		);
	}
};

export const handleIsReceivingEmailMessages = () => async (dispatch) => {
	try {
		// this is the current state of the user. we need to flip it.

		const res = await axios.post('/api/update-is-receiving-email-messages');

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (_err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleIsReceivingMentionEmails = () => async (dispatch) => {
	try {
		const res = await axios.post('/api/update-is-receiving-mention-emails');

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			dispatch(notification('Success', 'Updated your preferences.', false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (_err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleUpdateEmailDigestSettings =
	({ digestMode, digestDailyHour, digestScope }) =>
	async (dispatch) => {
		try {
			const res = await axios.post('/api/update-email-digest-settings', {
				digestMode,
				digestDailyHour,
				digestScope,
			});

			if (res.data && !res.data.error) {
				dispatch({ type: FETCH_USER, payload: res.data });
				dispatch(notification('Success', 'Updated your preferences.', false));
			} else {
				dispatch(
					notification(
						'Error',
						'There was an error updating your settings.',
						true
					)
				);
			}
		} catch (_err) {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	};

export const handleIsReceivingReviewEmail = () => async (dispatch) => {
	try {
		// this is the current state of the user. we need to flip it.

		const res = await axios.post('/api/update-is-receiving-review-email');

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleIsCollectingFeedback = () => async (dispatch) => {
	try {
		// this is the current state of the user. we need to flip it.

		const res = await axios.post('/api/update-is-collecting-feedback');

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleHasAutoReplyMessage = () => async (dispatch) => {
	try {
		const res = await axios.post('/api/update-has-auto-reply');

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleIsIncludingMediaLink = () => async (dispatch) => {
	try {
		const res = await axios.post('/api/update-is-including-media-link');

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleHasOutOfOfficeReplyMessage = () => async (dispatch) => {
	try {
		const res = await axios.post(
			'/api/update-has-out-of-office-auto-reply'
		);

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleHasMissedCallMessage = () => async (dispatch) => {
	try {
		const res = await axios.post('/api/update-has-missed-call-message');

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			dispatch(notification('Success', 'Updated your preferences.', false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleIsSmsAutoReplyMessage = () => async (dispatch) => {
	try {
		// this is the current state of the user. we need to flip it.

		const res = await axios.post('/api/update-is-sms-auto-reply-message');

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleIsWebchatAutoReplyMessage = () => async (dispatch) => {
	try {
		// this is the current state of the user. we need to flip it.
		const res = await axios.post(
			'/api/update-is-webchat-auto-reply-message'
		);

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleIsFacebookAutoReplyMessage = () => async (dispatch) => {
	try {
		// this is the current state of the user. we need to flip it.

		const res = await axios.post(
			'/api/update-is-facebook-auto-reply-message'
		);

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleIsInstagramAutoReplyMessage = () => async (dispatch) => {
	try {
		// this is the current state of the user. we need to flip it.

		const res = await axios.post(
			'/api/update-is-instagram-auto-reply-message'
		);

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleIsGoogleAutoReplyMessage = () => async (dispatch) => {
	try {
		// this is the current state of the user. we need to flip it.

		const res = await axios.post(
			'/api/update-is-google-auto-reply-message'
		);

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};

export const handleReferBusiness =
	({ businessName, contactName, email, phone }) =>
	async (dispatch) => {
		try {
			// refer a business to chekkit
			const res = await axios.post('/api/refer-a-business', {
				businessName,
				contactName,
				email,
				phone,
			});

			if (res.data && !res.data.error) {
				const title = 'Success';
				const message = 'You referred a business to Chekkit.';
				return dispatch(notification(title, message, false));
			}
			return dispatch(
				notification(
					'Error',
					'We could not refer your business to Chekkit.',
					true
				)
			);
		} catch (err) {
			return dispatch(
				notification(
					'Error',
					'We could not refer your business to Chekkit.',
					true
				)
			);
		}
	};

export const handleIsReceivingPaidInvoicesEmail = () => async (dispatch) => {
	try {
		// this is the current state of the user. we need to flip it.

		const res = await axios.post(
			'/api/payments/settings/update-is-receiving-paid-invoices-email'
		);

		if (res.data && !res.data.error) {
			dispatch({ type: FETCH_USER, payload: res.data });
			const title = 'Success';
			const message = 'Updated your preferences.';
			dispatch(notification(title, message, false));
		} else {
			dispatch(
				notification(
					'Error',
					'There was an error updating your settings.',
					true
				)
			);
		}
	} catch (err) {
		dispatch(
			notification(
				'Error',
				'There was an error updating your settings.',
				true
			)
		);
	}
};
